import React, { useEffect, useReducer, useState } from 'react';
import api from '../api';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { getError } from '../utils';
import Container from 'react-bootstrap/Container';
import Form from 'react-bootstrap/Form';
import LoadingLogo from '../components/general/LoadingLogo';
import MessageBox from '../components/general/MessageBox';
import Button from 'react-bootstrap/Button';
import Editor from '@monaco-editor/react';
import Table from 'react-bootstrap/Table';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';
import { OverlayTrigger, Popover } from 'react-bootstrap';
import {
  buildPunchingHolePositions,
  recalcKeycapAdjustments,
  getSlotCenter,
  getSlotPrintArea,
  PAPER_SIZES,
} from '../utils/moldGeometry';
import {
  PunchingHoles,
  MoldBorder,
  ScrewHoles,
} from '../components/customizer/shared/MoldPrintMarks';
import { calculateKeycapSizes } from '../utils/keycapSizes';

const reducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loading: true };
    case 'FETCH_SUCCESS':
      return { ...state, loading: false };
    case 'FETCH_FAIL':
      return { ...state, loading: false, error: action.payload };

    case 'UPDATE_REQUEST':
      return { ...state, loadingUpdate: true };
    case 'UPDATE_SUCCESS':
      return { ...state, loadingUpdate: false };
    case 'UPDATE_FAIL':
      return { ...state, loadingUpdate: false };

    default:
      return state;
  }
};

export default function MoldEditScreen() {
  const navigate = useNavigate();
  const params = useParams(); // safes the parameters (dynamic segments), defined in the URL, into the params variable: /mold/:id
  const { id: moldId } = params; // deconstracts the id out of the params variable and its name is set to moldId

  const [{ loading, error, loadingUpdate }, dispatch] = useReducer(reducer, {
    loading: true,
    error: '',
  });

  const [name, setName] = useState('');
  const [size, setSize] = useState('');
  const [profile, setProfile] = useState('');
  const [keycapManufacturer, setKeycapManufacturer] = useState('');
  const [isActive, setIsActive] = useState(false);
  const [scaling, setScaling] = useState({
    punchingHoles: 1,
    keycaps: 1,
  });
  const [punchingHoleXAdjustments, setPunchingHoleXAdjustments] = useState({
    topLeft: 0,
    topRight: 0,
    bottomLeft: 0,
    bottomRight: 0,
  });
  const [mold, setMold] = useState([]);
  const [readOnly, setReadOnly] = useState(true);
  const [keycapFrequencies, setKeycapFrequencies] = useState([]);
  const [keycapFilter, setKeycapFilter] = useState('Uniform');

  // states to allow changing the input value without changing the state immediatly:
  const [topLeftInput, setTopLeftInput] = useState(
    punchingHoleXAdjustments.topLeft.toString()
  );
  const [topRightInput, setTopRightInput] = useState(
    punchingHoleXAdjustments.topRight.toString()
  );
  const [bottomLeftInput, setBottomLeftInput] = useState(
    punchingHoleXAdjustments.bottomLeft.toString()
  );
  const [bottomRightInput, setBottomRightInput] = useState(
    punchingHoleXAdjustments.bottomRight.toString()
  );

  const [dimensions, setDimensions] = useState(PAPER_SIZES.A3); // the preview's sheet, as the size select picks it

  const handleSizeChange = (e) => {
    const newSize = e.target.value;
    setSize(newSize); // Update size of the mold document
    setDimensions(PAPER_SIZES[newSize]); // Update dimensions of the svg
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const { data } = await api.get(`/api/molds/${moldId}`);

        setName(data.name);
        setSize(data.size);
        setProfile(data.profile);
        setKeycapManufacturer(data.keycapManufacturer);
        setIsActive(data.isActive);
        setScaling({
          punchingHoles: data.scaling?.punchingHoles ?? 1,
          keycaps: data.scaling?.keycaps ?? 1,
        });
        setPunchingHoleXAdjustments({
          topLeft: data.punchingHoleXAdjustments?.topLeft ?? 0,
          topRight: data.punchingHoleXAdjustments?.topRight ?? 0,
          bottomLeft: data.punchingHoleXAdjustments?.bottomLeft ?? 0,
          bottomRight: data.punchingHoleXAdjustments?.bottomRight ?? 0,
        });
        setMold(data.mold);
        setKeycapFrequencies(calculateKeycapSizes([data.mold])); // the mold's slots count as one row of keycaps: a mold holds no spacers, homing bars or empty objects, and an XDA mold has one slot for a 1x2 and a 2x1, so the layout function lists the slots as the screen's own copy did (user decision, Oct 2026)

        // also set the initial punchingHoleXAdjustments input values:
        setTopLeftInput(
          (data.punchingHoleXAdjustments?.topLeft ?? 0).toString()
        );
        setTopRightInput(
          (data.punchingHoleXAdjustments?.topRight ?? 0).toString()
        );
        setBottomLeftInput(
          (data.punchingHoleXAdjustments?.bottomLeft ?? 0).toString()
        );
        setBottomRightInput(
          (data.punchingHoleXAdjustments?.bottomRight ?? 0).toString()
        );

        dispatch({ type: 'FETCH_SUCCESS' });
      } catch (err) {
        dispatch({
          type: 'FETCH_FAIL',
          payload: getError(err),
        });
      }
    };
    fetchData(); // fetch the mold
  }, [moldId]);

  const submitHandler = async (e) => {
    e.preventDefault(); // used, so the page doesn't refresh, when the submit button is clicked
    try {
      dispatch({ type: 'UPDATE_REQUEST' });

      await api.put(`/api/molds/${moldId}`, {
        _id: moldId,
        name,
        size,
        profile,
        keycapManufacturer,
        isActive,
        scaling,
        punchingHoleXAdjustments,
        mold,
      });
      dispatch({
        type: 'UPDATE_SUCCESS',
      });
      toast.success('Mold saved.');
      navigate('/admin/molds');
    } catch (err) {
      toast.error(getError(err));
      dispatch({ type: 'UPDATE_FAIL' });
    }
  }; // a put request to update the mold in the database

  const handleChange = (value) => {
    try {
      const newMold = JSON.parse(value);
      setMold(newMold); // Updates the mold state when the user edits the json
      setKeycapFrequencies(calculateKeycapSizes([newMold]));
    } catch (error) {
      return null; // don't update the mold if the json is invalid, so that during editing no errors are thrown
    }
  };

  const toggleReadOnly = () => {
    setReadOnly(!readOnly);
  };

  const punchingHolePositions = React.useMemo(
    () =>
      buildPunchingHolePositions(
        scaling.punchingHoles,
        punchingHoleXAdjustments
      ),
    [scaling.punchingHoles, punchingHoleXAdjustments]
  ); // calculates new punching hole positions every time the scaling or punchingHoleXAdjustments change

  useEffect(() => {
    setMold((prev) => recalcKeycapAdjustments(scaling.keycaps, prev));
  }, [scaling.keycaps]); // calculate new adjustment values for all keycaps inside the mold array, whenever the value of scaling.keycaps changes

  return (
    <Container>
      <Container>
        <title>Edit Mold</title>

        <h1 className="screen-heading">Edit Mold</h1>

        {loading || loadingUpdate ? (
          <div className="overlay">
            <LoadingLogo />
          </div>
        ) : error ? (
          <MessageBox className="danger-alert">{error}</MessageBox>
        ) : (
          <Form onSubmit={submitHandler}>
            <Form.Group className="mb-3" controlId="name">
              <Form.Label>Name</Form.Label>
              <Form.Control
                className="mobile-device-compatible"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </Form.Group>

            <Form.Group className="mb-3" controlId="size">
              <Form.Label>Size</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={size}
                onChange={handleSizeChange}
                required
              >
                <option className="option-tag" value="A3">
                  A3 (420mm x 297mm)
                </option>
                <option className="option-tag" value="A3+">
                  A3+ (483mm x 329mm)
                </option>
                <option className="option-tag" value="A2">
                  A2 (594mm x 420mm)
                </option>
                <option className="option-tag" value="A2+">
                  A2+ (624mm x 456mm)
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="profile">
              <Form.Label>Profile</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={profile}
                onChange={(e) => setProfile(e.target.value)}
                required
              >
                <option className="option-tag" value="XDA">
                  XDA
                </option>
                <option className="option-tag" value="DSA">
                  DSA
                </option>
                <option className="option-tag" value="OEM">
                  OEM
                </option>
                <option className="option-tag" value="Cherry">
                  Cherry
                </option>
                <option className="option-tag" value="SA">
                  SA
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="keycapManufacturer">
              <Form.Label>Keycap Manufacturer</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={keycapManufacturer}
                onChange={(e) => setKeycapManufacturer(e.target.value)}
                required
              >
                <option className="option-tag" value="Tecsee">
                  Tecsee
                </option>
              </Form.Select>
            </Form.Group>

            <Form.Group className="mb-3" controlId="active">
              <Form.Label>Active</Form.Label>
              <Form.Select
                className="mobile-device-compatible"
                value={isActive}
                onChange={(e) => setIsActive(e.target.value)}
                required
              >
                <option className="option-tag" value={true}>
                  Yes
                </option>
                <option className="option-tag" value={false}>
                  No
                </option>
              </Form.Select>
            </Form.Group>

            <div
              style={{
                border: '2px solid grey',
                borderRadius: '5px',
                padding: '25px',
                margin: '15px 0',
              }}
            >
              <h2 className="d-flex justify-content-center align-items-center mb-5">
                Printer Distortion Correction Parameters
                <OverlayTrigger
                  trigger="click"
                  rootClose // close the popover when the user clicks outside of it
                  placement="right"
                  overlay={
                    <Popover id="popover-password">
                      <Popover.Body>
                        These parameters change the positions of the punching
                        holes and keycaps on the printed document. These
                        settings are needed because the printer (Epson L18050)
                        does not print a 100% accurate result. In general the
                        print is a bit upscaled (the print is a bit larger than
                        the actual file). Also near the edges of the printed
                        document, the printed result becomes more inaccurate and
                        additionally applies a slight trapezoid distortion
                        (which is only really noticable for the punching holes).
                      </Popover.Body>
                    </Popover>
                  }
                >
                  <Button className="info-popover">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="16"
                      height="16"
                      fill="#707070"
                      className="bi bi-info-circle"
                      viewBox="0 0 16 16"
                    >
                      <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                      <path
                        d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                          l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                          1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                          -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                          1 1 0 0 1 2 0z"
                      />
                    </svg>
                  </Button>
                </OverlayTrigger>
              </h2>

              <div className="d-flex" style={{ gap: '25px' }}>
                <Form.Group
                  className="mb-3 w-50"
                  controlId="punchingHolesScaling"
                >
                  <Form.Label className="d-flex align-items-center">
                    Punching Holes Scaling
                    <OverlayTrigger
                      trigger="click"
                      rootClose // close the popover when the user clicks outside of it
                      placement="right"
                      overlay={
                        <Popover id="popover-password">
                          <Popover.Body>
                            This setting applies scaling to the punching holes
                            as a group.
                            <br />
                            <br />
                            It does not scale the size of the punching holes,
                            only the distance between the center of the document
                            and the punching holes themselfs.
                            <br />
                            <br />
                            For example, if a scaling of 99.89% is applied, each
                            punching hole slightly moves to the center of the
                            document. The further a punching hole is positioned
                            away from the center of the document, the more it
                            moves into it's direction. If a punching hole would
                            be placed exactly in the center of the document, the
                            scaling would not change it's position. This is
                            because the scaling percentage is always applied to
                            the distance between the punching hole and the
                            center of the document.
                            <br />
                            <br />
                            This setting is needed, because the printer is not
                            very accurate and the printed document has a bit of
                            distortion.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <Button className="info-popover">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#707070"
                          className="bi bi-info-circle"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                          <path
                            d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                          l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                          1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                          -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                          1 1 0 0 1 2 0z"
                          />
                        </svg>
                      </Button>
                    </OverlayTrigger>
                  </Form.Label>

                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    step="0.0001"
                    value={scaling.punchingHoles}
                    onChange={(e) => {
                      const number = parseFloat(e.target.value);
                      if (!Number.isFinite(number)) return; // an emptied field parsed to NaN, which went into every hole's position until a number was typed again (fixed Oct 2026)
                      setScaling((prev) => ({
                        ...prev,
                        punchingHoles: number,
                      }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // prevent form submit
                        e.currentTarget.blur(); // blur the input
                      }
                    }}
                  />
                </Form.Group>

                <Form.Group className="mb-3 w-50" controlId="keycapsScaling">
                  <Form.Label className="d-flex align-items-center">
                    Keycaps Scaling
                    <OverlayTrigger
                      trigger="click"
                      rootClose // close the popover when the user clicks outside of it
                      placement="right"
                      overlay={
                        <Popover id="popover-password">
                          <Popover.Body>
                            This setting applies scaling to the keycaps as a
                            group.
                            <br />
                            <br />
                            It does not scale the size of the keycaps, only the
                            distance between the center of the document and the
                            keycaps themselfs.
                            <br />
                            <br />
                            For example, if a scaling of 99.6% is applied, each
                            keycap slightly moves to the center of the document.
                            The further the keycaps is positioned away from the
                            center of the document, the more it moves into it's
                            direction. If a keycap would be placed exactly in
                            the center of the document, the scaling would not
                            change it's position. This is because the scaling
                            percentage is always applied to the distance between
                            the punching hole and the center of the document.
                            <br />
                            <br />
                            This setting is needed, because the printer is not
                            very accurate and the printed document has a bit of
                            distortion.
                          </Popover.Body>
                        </Popover>
                      }
                    >
                      <Button className="info-popover">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          fill="#707070"
                          className="bi bi-info-circle"
                          viewBox="0 0 16 16"
                        >
                          <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                          <path
                            d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                          l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                          1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                          -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                          1 1 0 0 1 2 0z"
                          />
                        </svg>
                      </Button>
                    </OverlayTrigger>
                  </Form.Label>

                  <Form.Control
                    className="mobile-device-compatible"
                    type="number"
                    step="0.0001"
                    value={scaling.keycaps}
                    onChange={(e) => {
                      const number = parseFloat(e.target.value);
                      if (!Number.isFinite(number)) return; // an emptied field parsed to NaN, which went into every slot's offsets and the preview until a number was typed again (fixed Oct 2026)
                      setScaling((prev) => ({ ...prev, keycaps: number }));
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault(); // prevent form submit
                        e.currentTarget.blur(); // blur the input
                      }
                    }}
                  />
                </Form.Group>
              </div>

              <div className="divider"></div>

              <Form.Group
                className="mb-3"
                controlId="topLeftPunchingHoleXAdjustment"
              >
                <Form.Label className="d-flex align-items-center">
                  Punching Holes X-Adjustment
                  <OverlayTrigger
                    trigger="click"
                    rootClose // close the popover when the user clicks outside of it
                    placement="right"
                    overlay={
                      <Popover id="popover-password">
                        <Popover.Body>
                          This setting moves the position of the punching holes
                          along the x axis. The values are in mm.
                          <br />
                          <br />
                          This is used to correct the trapezoid distortion
                          created by the printer. We only need to adjust the
                          punching holes, as the distortion only happens near
                          the edges of the printing file.
                        </Popover.Body>
                      </Popover>
                    }
                  >
                    <Button className="info-popover">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        fill="#707070"
                        className="bi bi-info-circle"
                        viewBox="0 0 16 16"
                      >
                        <path d="M8 15A7 7 0 1 1 8 1a7 7 0 0 1 0 14zm0 1A8 8 0 1 0 8 0a8 8 0 0 0 0 16z" />
                        <path
                          d="m8.93 6.588-2.29.287-.082.38.45.083c.294.07.352.176.288.469
                            l-.738 3.468c-.194.897.105 1.319.808 1.319.545 0 1.178-.252
                            1.465-.598l.088-.416c-.2.176-.492.246-.686.246-.275 0
                            -.375-.193-.304-.533L8.93 6.588zM9 4.5a1 1 0 1 1-2 0
                            1 1 0 0 1 2 0z"
                        />
                      </svg>
                    </Button>
                  </OverlayTrigger>
                </Form.Label>

                <div className="d-flex mt-3" style={{ gap: '25px' }}>
                  <div style={{ width: '25%' }}>
                    <p
                      style={{
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '12px',
                        marginBottom: '0px',
                      }}
                    >
                      Top-Left
                    </p>

                    <Form.Control
                      className="mobile-device-compatible"
                      type="number"
                      step="0.01"
                      value={topLeftInput}
                      onChange={(e) => setTopLeftInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseFloat(topLeftInput);
                        const isInvalid = isNaN(parsed);

                        if (isInvalid) {
                          setTopLeftInput('0');
                        } else {
                          setTopLeftInput(parsed.toString());
                        }

                        setPunchingHoleXAdjustments((prev) => ({
                          ...prev,
                          topLeft: isInvalid ? 0 : parsed,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // prevent form submit
                          e.currentTarget.blur(); // blur the input
                        }
                      }}
                    />
                  </div>

                  <div style={{ width: '25%' }}>
                    <p
                      style={{
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '12px',
                        marginBottom: '0px',
                      }}
                    >
                      Top-Right
                    </p>

                    <Form.Control
                      className="mobile-device-compatible"
                      type="number"
                      step="0.01"
                      value={topRightInput}
                      onChange={(e) => setTopRightInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseFloat(topRightInput);
                        const isInvalid = isNaN(parsed);

                        if (isInvalid) {
                          setTopRightInput('0');
                        } else {
                          setTopRightInput(parsed.toString());
                        }

                        setPunchingHoleXAdjustments((prev) => ({
                          ...prev,
                          topRight: isInvalid ? 0 : parsed,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // prevent form submit
                          e.currentTarget.blur(); // blur the input
                        }
                      }}
                    />
                  </div>

                  <div style={{ width: '25%' }}>
                    <p
                      style={{
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '12px',
                        marginBottom: '0px',
                      }}
                    >
                      Bottom-Left
                    </p>

                    <Form.Control
                      className="mobile-device-compatible"
                      type="number"
                      step="0.01"
                      value={bottomLeftInput}
                      onChange={(e) => setBottomLeftInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseFloat(bottomLeftInput);
                        const isInvalid = isNaN(parsed);

                        if (isInvalid) {
                          setBottomLeftInput('0');
                        } else {
                          setBottomLeftInput(parsed.toString());
                        }

                        setPunchingHoleXAdjustments((prev) => ({
                          ...prev,
                          bottomLeft: isInvalid ? 0 : parsed,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // prevent form submit
                          e.currentTarget.blur(); // blur the input
                        }
                      }}
                    />
                  </div>

                  <div style={{ width: '25%' }}>
                    <p
                      style={{
                        textAlign: 'center',
                        width: '100%',
                        fontSize: '12px',
                        marginBottom: '0px',
                      }}
                    >
                      Bottom-Right
                    </p>

                    <Form.Control
                      className="mobile-device-compatible"
                      type="number"
                      step="0.01"
                      value={bottomRightInput}
                      onChange={(e) => setBottomRightInput(e.target.value)}
                      onBlur={() => {
                        const parsed = parseFloat(bottomRightInput);
                        const isInvalid = isNaN(parsed);

                        if (isInvalid) {
                          setBottomRightInput('0');
                        } else {
                          setBottomRightInput(parsed.toString());
                        }

                        setPunchingHoleXAdjustments((prev) => ({
                          ...prev,
                          bottomRight: isInvalid ? 0 : parsed,
                        }));
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault(); // prevent form submit
                          e.currentTarget.blur(); // blur the input
                        }
                      }}
                    />
                  </div>
                </div>
              </Form.Group>
            </div>

            <Form.Group
              className="mb-3 d-flex justify-content-between align-center"
              controlId="layout"
            >
              <Form.Label>Layout</Form.Label>
              <Button
                style={{ fontSize: '12px' }}
                className="no-css"
                variant="none"
                onClick={toggleReadOnly}
              >
                {readOnly ? (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="currentColor"
                    className="bi bi-pencil-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M15.502 1.94a.5.5 0 0 1 0 .706L14.459 3.69l-2-2L13.502.646a.5.5 0 0 1 .707 0l1.293 1.293zm-1.75 2.456-2-2L4.939 9.21a.5.5 0 0 0-.121.196l-.805 2.414a.25.25 0 0 0 .316.316l2.414-.805a.5.5 0 0 0 .196-.12l6.813-6.814z" />
                    <path
                      fillRule="evenodd"
                      d="M1 13.5A1.5 1.5 0 0 0 2.5 15h11a1.5 1.5 0 0 0 1.5-1.5v-6a.5.5 0 0 0-1 0v6a.5.5 0 0 1-.5.5h-11a.5.5 0 0 1-.5-.5v-11a.5.5 0 0 1 .5-.5H9a.5.5 0 0 0 0-1H2.5A1.5 1.5 0 0 0 1 2.5z"
                    />
                  </svg>
                ) : (
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="20"
                    height="20"
                    fill="red"
                    className="bi bi-x-square"
                    viewBox="0 0 16 16"
                  >
                    <path d="M14 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H2a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1zM2 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2z" />
                    <path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708" />
                  </svg>
                )}
              </Button>
            </Form.Group>

            <div className="editor-container">
              <div className="ide-wrapper">
                <Editor
                  height="100%"
                  defaultLanguage="json"
                  value={JSON.stringify(mold, null, 2)}
                  onChange={handleChange}
                  options={{
                    readOnly: readOnly,
                    minimap: { enabled: true },
                    lineNumbers: 'on',
                    automaticLayout: true,
                    fontSize: 14,
                    wordWrap: 'on',
                    scrollBeyondLastLine: false,
                    quickSuggestions: true,
                    tabSize: 2,
                    bracketPairColorization: true,
                    renderWhitespace: false,
                    mouseWheelZoom: true,
                    smoothScrolling: true,
                    cursorBlinking: 'smooth',
                    cursorStyle: 'line',
                  }}
                />
              </div>

              <Row className="mt-4">
                <Col md={10}>
                  <div
                    className="d-flex justify-content-center"
                    style={{
                      position: 'relative',
                    }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg" // Ensure correct namespace
                      viewBox={`0 0 ${dimensions.width} ${dimensions.height}`} // Dynamic viewBox
                      style={{
                        maxWidth: dimensions.width * 3,
                        width: '100%',
                        height: 'auto',
                        overflow: 'visible', // To prevent clipping (is needed to ensure that the svg is not being clipped at the bottom and right side due to rendering nuances like rounding errors and anti-aliasing)
                      }}
                      version="1.1"
                      id="customDesignedKeycapSet"
                    >
                      {/* Keycaps: */}
                      <g id="g7" fill="none" fillRule="evenodd">
                        {mold.map((keycap, index) => {
                          const { cx, cy } = getSlotCenter(keycap); // the stored centre with the scaling offset and the manual adjustment applied

                          return (
                            <g key={index}>
                              {/* 1. Total Area for the Keycap Color: */}
                              <rect
                                {...getSlotPrintArea(keycap)}
                                fill="lightblue"
                              />

                              {/* 2. Actual Keycap: */}
                              <rect
                                x={cx - (keycap.width * 18) / 2}
                                y={cy - (keycap.height * 18) / 2}
                                width={keycap.width * 18}
                                height={keycap.height * 18}
                                fill="black"
                              />

                              {/* 3. Center of the Keycap: */}
                              <circle cx={cx} cy={cy} r="0.5" fill="white" />
                            </g>
                          );
                        })}
                      </g>

                      {size === 'A3' ? (
                        <g>
                          <PunchingHoles
                            positions={punchingHolePositions}
                            withRings
                          />
                          <MoldBorder />
                          <ScrewHoles />
                        </g>
                      ) : null}
                    </svg>
                  </div>
                </Col>
                <Col
                  className="d-flex align-items-center"
                  style={{ fontSize: '12px' }}
                  md={2}
                >
                  <Table striped bordered hover style={{ marginBottom: '0px' }}>
                    <thead>
                      <tr>
                        <th>Keycap</th>
                        <th>Frequency</th>
                      </tr>
                    </thead>
                    <tbody>
                      {keycapFilter === 'Uniform'
                        ? keycapFrequencies.keycapSizes.map(
                            ([size, frequency]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>{frequency}</td>
                              </tr>
                            )
                          )
                        : keycapFrequencies.keycapSizesWithRow.map(
                            ([size, frequency]) => (
                              <tr key={size}>
                                <td>{size}</td>
                                <td>{frequency}</td>
                              </tr>
                            )
                          )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row className="mt-1">
                <Col md={10}></Col>
                <Col md={2}>
                  <div className="d-flex justify-content-end">
                    <Button
                      className="mx-2"
                      onClick={() => setKeycapFilter('Uniform')}
                      variant={keycapFilter === 'Uniform' ? 'dark' : 'light'}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-list"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M2.5 12a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h10a.5.5 0 0 1 0 1H3a.5.5 0 0 1-.5-.5"
                        />
                      </svg>
                    </Button>
                    <Button
                      className="mx-2"
                      onClick={() => setKeycapFilter('Row')}
                      variant={keycapFilter === 'Row' ? 'dark' : 'light'}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="22"
                        height="22"
                        fill="currentColor"
                        className="bi bi-list-ol"
                        viewBox="0 0 16 16"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5 11.5a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5m0-4a.5.5 0 0 1 .5-.5h9a.5.5 0 0 1 0 1h-9a.5.5 0 0 1-.5-.5"
                        />
                        <path d="M1.713 11.865v-.474H2c.217 0 .363-.137.363-.317 0-.185-.158-.31-.361-.31-.223 0-.367.152-.373.31h-.59c.016-.467.373-.787.986-.787.588-.002.954.291.957.703a.595.595 0 0 1-.492.594v.033a.615.615 0 0 1 .569.631c.003.533-.502.8-1.051.8-.656 0-1-.37-1.008-.794h.582c.008.178.186.306.422.309.254 0 .424-.145.422-.35-.002-.195-.155-.348-.414-.348h-.3zm-.004-4.699h-.604v-.035c0-.408.295-.844.958-.844.583 0 .96.326.96.756 0 .389-.257.617-.476.848l-.537.572v.03h1.054V9H1.143v-.395l.957-.99c.138-.142.293-.304.293-.508 0-.18-.147-.32-.342-.32a.33.33 0 0 0-.342.338zM2.564 5h-.635V2.924h-.031l-.598.42v-.567l.629-.443h.635z" />
                      </svg>
                    </Button>
                  </div>
                </Col>
              </Row>
            </div>

            <div className="d-flex justify-content-center align-items-center mt-5">
              <Button
                variant="secondary"
                className="mx-3"
                disabled={loadingUpdate}
                type="submit"
              >
                {/* disabled prop is set to loadingUpdate, because it should not be possible, to click the update button multiple times */}
                Save
              </Button>
            </div>
          </Form>
        )}
      </Container>
    </Container>
  );
}
